import { useState, useEffect, useMemo } from 'react';
import { MessageSquare } from 'lucide-react';

import { Button } from '@/components/ui/button';

import type { PostListItem } from '../types';
import { useForum } from '../useForum';
import { useServerPostSearch } from '../useServerPostSearch';
import { CreatePostForm } from './CreatePostForm';
import { ForumCardSkeleton } from './ForumCardSkeleton';
import { ThreadRow } from './ThreadRow';
import {
  forumSearchTerm,
  isHotThread,
  matchesSearch,
  searchesOnServer,
  threadTags,
} from '../forum-display';

/**
 * The body of a space: the discussion list and the rail beside it.
 *
 * Rebuilt to the admin app's forum redesign (ai2aim-ems 3bb83bc), whose
 * SpaceView owns exactly this. A two-column grid of cards became one enclosing
 * card of dense rows, because a grid of cards makes every thread look like a
 * product tile and puts a hard edge around a sentence.
 *
 * THE TABS ARE THAT DESIGN'S, including what each one means:
 *
 *   All       no filter
 *   Hot       upvotes >= HOT_UPVOTE_THRESHOLD, read from the shared rule so a
 *             thread cannot be hot here and cold on the index
 *   Tagged    the body carries at least one hashtag
 *   Trending  every thread, re-sorted by upvotes rather than filtered
 *
 * What was here instead was "Badges", "Topics" and "Famous", and none of the
 * three named what it did: Badges filtered on score, Topics on hashtags, and
 * Famous re-sorted by the same score Badges filtered on. Two names for one
 * rule, and a third that described none of them.
 *
 * Picking a topic chip narrows the list to that tag and switches to Tagged, so
 * the tab row always says what the list is currently showing. Picking it again
 * clears it — the chip is a toggle, not a one-way trip that needs the Clear
 * link hunted down.
 */
interface ThreadListProps {
  subforumId: string;
  searchQuery?: string;
  showCreateForm?: boolean;
  onCloseCreateForm?: () => void;
}

type Tab = 'all' | 'hot' | 'tagged' | 'trending';

const TABS: { id: Tab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'hot', label: 'Hot' },
  { id: 'tagged', label: 'Tagged' },
  { id: 'trending', label: 'Trending' },
];

const POSTS_PER_PAGE = 10;

const LINE = 'rgba(var(--ink-rgb),.13)';
const MUTED = 'rgba(var(--ink-rgb),.62)';

function RailCard({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="flex flex-col gap-3 rounded-xl px-5 py-4"
      style={{ border: `1px solid ${LINE}`, background: 'var(--paper2)' }}
    >
      <span
        className="text-[10px] font-bold uppercase tracking-wider"
        style={{ color: MUTED }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

export function ThreadList({
  subforumId,
  searchQuery = '',
  showCreateForm = false,
  onCloseCreateForm,
}: ThreadListProps) {
  const { getPostsBySubforum, getSubforum, fetchPosts, setSortOption, state } =
    useForum();
  const [currentPage, setCurrentPage] = useState(1);
  const [tab, setTab] = useState<Tab>('all');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [sort, setSort] = useState<'new' | 'top'>('new');

  const space = getSubforum(subforumId);
  const threads = getPostsBySubforum(subforumId);

  const spaceTags = useMemo(() => {
    const all = threads.flatMap(threadTags);
    return [...new Set(all)].slice(0, 12);
  }, [threads]);

  const hotCount = useMemo(() => threads.filter(isHotThread).length, [threads]);

  // A search runs on the server once the API can (its rows carry `excerpt`),
  // over the whole space rather than the loaded rows. Until the first answer
  // for a term arrives, and on an API that cannot search, the loaded rows are
  // filtered here as before. The topics and the hot count keep reading the
  // unsearched list.
  const searchResults = useServerPostSearch({
    query: searchQuery,
    enabled: searchesOnServer(threads),
    subforumId,
    sort,
  });

  const visible = useMemo(() => {
    let rows: PostListItem[] = searchResults ?? threads;
    if (tab === 'hot') rows = rows.filter(isHotThread);
    if (tab === 'tagged') rows = rows.filter((t) => threadTags(t).length > 0);
    if (activeTag) rows = rows.filter((t) => threadTags(t).includes(activeTag));
    if (tab === 'trending') rows = [...rows].sort((a, b) => b.upvotes - a.upvotes);
    if (searchResults) return rows;
    const q = forumSearchTerm(searchQuery).toLowerCase();
    if (!q) return rows;
    return rows.filter((t) => matchesSearch(t, q));
  }, [threads, searchResults, tab, activeTag, searchQuery]);

  useEffect(() => {
    fetchPosts(subforumId);
    setCurrentPage(1);
  }, [subforumId, fetchPosts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [tab, activeTag, searchQuery, searchResults]);

  useEffect(() => {
    setSortOption(sort === 'top' ? 'top' : 'new');
  }, [sort, setSortOption]);

  const isLoading = state.loading.posts.has(subforumId);
  const totalPages = Math.ceil(visible.length / POSTS_PER_PAGE);
  const posts = visible.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE,
  );

  const pickTag = (tag: string) => {
    const next = activeTag === tag ? null : tag;
    setActiveTag(next);
    setTab(next ? 'tagged' : 'all');
  };

  return (
    <div className="flex w-full flex-wrap items-start gap-[22px]">
      <div className="flex min-w-0 flex-1 basis-[520px] flex-col gap-5">
        {showCreateForm && onCloseCreateForm ? (
          <CreatePostForm subforumId={subforumId} onClose={onCloseCreateForm} />
        ) : null}

        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex gap-6" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => {
                  setTab(t.id);
                  if (t.id !== 'tagged') setActiveTag(null);
                }}
                className="dc-tabline pb-3 text-[12.5px] font-semibold"
                data-active={tab === t.id ? '' : undefined}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div
            className="flex gap-1 rounded-lg p-[3px]"
            style={{ background: 'rgba(var(--ink-rgb),.06)' }}
            role="group"
            aria-label="Sort discussions"
          >
            {(
              [
                { key: 'new', label: 'Recent' },
                { key: 'top', label: 'Top' },
              ] as const
            ).map((s) => (
              <button
                key={s.key}
                type="button"
                aria-pressed={sort === s.key}
                onClick={() => setSort(s.key)}
                className="dc-seg rounded-md px-3.5 py-[7px] text-xs font-semibold"
                data-active={sort === s.key ? '' : undefined}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {activeTag && (
          <div className="flex items-center gap-2 text-xs" style={{ color: MUTED }}>
            <span>
              Filtered by{' '}
              <strong className="font-semibold" style={{ color: 'var(--ink)' }}>
                {activeTag}
              </strong>
            </span>
            <button
              type="button"
              onClick={() => {
                setActiveTag(null);
                setTab('all');
              }}
              className="font-semibold hover:underline"
              style={{ color: 'var(--acc2)' }}
            >
              Clear
            </button>
          </div>
        )}

        <div
          className="overflow-hidden rounded-xl"
          style={{ border: `1px solid ${LINE}`, background: 'var(--paper2)' }}
        >
          <div
            className="flex items-center justify-between gap-3 px-5 pb-3.5 pt-4"
            style={{ borderBottom: `1px solid ${LINE}` }}
          >
            <div>
              <div
                className="text-sm font-semibold"
                style={{ color: 'var(--ink)' }}
              >
                Discussions
              </div>
              <div className="mt-1.5 text-[11px]" style={{ color: MUTED }}>
                {visible.length}{' '}
                {visible.length === 1 ? 'discussion' : 'discussions'}
              </div>
            </div>
          </div>

          {isLoading && threads.length === 0 ? (
            <div className="flex flex-col gap-4 p-5">
              {Array.from({ length: 4 }).map((_, i) => (
                <ForumCardSkeleton key={i} />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-5 py-12 text-center">
              <MessageSquare
                size={28}
                style={{ color: 'rgba(var(--ink-rgb),.3)' }}
              />
              <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
                {searchQuery.trim()
                  ? 'No discussions match that search'
                  : tab === 'hot'
                    ? 'Nothing is hot in this space yet'
                    : tab === 'tagged'
                      ? 'No tagged discussions here'
                      : 'No discussions yet'}
              </p>
              <p className="text-xs" style={{ color: MUTED }}>
                Start the first conversation in this space.
              </p>
            </div>
          ) : (
            posts.map((post) => (
              <ThreadRow
                key={post.id}
                post={post}
                space={space}
                variant="space"
              />
            ))
          )}

          {totalPages > 1 && (
            <div
              className="flex items-center justify-center gap-3 px-5 py-2.5"
              style={{
                borderTop: `1px solid ${LINE}`,
                background: 'rgba(var(--ink-rgb),.03)',
              }}
            >
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1 || isLoading}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-xs font-medium" style={{ color: MUTED }}>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages || isLoading}
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      <aside className="flex w-full min-w-0 max-w-[300px] flex-1 basis-[260px] flex-col gap-4">
        {space && (
          <RailCard label="About this space">
            {space.description && (
              <span
                className="text-xs leading-[1.55]"
                style={{ color: MUTED, textWrap: 'pretty' }}
              >
                {space.description}
              </span>
            )}
            <div className="flex gap-6 pt-0.5">
              <div>
                <div
                  className="text-[14px] font-bold leading-none"
                  style={{ color: 'var(--ink)' }}
                >
                  {space.postCount}
                </div>
                <div className="mt-1.5 text-[11px]" style={{ color: MUTED }}>
                  {space.postCount === 1 ? 'discussion' : 'discussions'}
                </div>
              </div>
              <div>
                <div
                  className="text-[14px] font-bold leading-none"
                  style={{ color: 'var(--ink)' }}
                >
                  {hotCount}
                </div>
                <div className="mt-1.5 text-[11px]" style={{ color: MUTED }}>
                  hot
                </div>
              </div>
            </div>
            <span className="text-[11px]" style={{ color: MUTED }}>
              /forum/{space.slug}
            </span>
          </RailCard>
        )}

        {spaceTags.length > 0 && (
          <RailCard label="Topics in this space">
            <div className="flex flex-wrap gap-1.5">
              {spaceTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  aria-pressed={activeTag === tag}
                  onClick={() => pickTag(tag)}
                  className="dc-tagchip rounded-md px-2.5 py-1 text-xs font-medium"
                  data-active={activeTag === tag ? '' : undefined}
                >
                  {tag}
                </button>
              ))}
            </div>
          </RailCard>
        )}
      </aside>
    </div>
  );
}
